import type { DateString } from '@repo/utils';

/** Un mese di calendario: l'unità con cui l'API aggrega l'utilizzo. */
export interface UsagePeriod {
  year: number;
  month: number;
}

const MONTHS_PER_YEAR = 12;
/** Anni proposti nel selettore: il corrente e i quattro precedenti. */
export const USAGE_YEARS_SHOWN = 5;

const MONTH_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const;

const MONTH_SHORT_KEYS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const;

/** I mesi dell'anno, da 1 a 12. */
export const USAGE_MONTHS: readonly number[] = MONTH_KEYS.map(
  (_, index) => index + 1
);

/** `months.march`: il nome del mese nei file di traduzione comuni. */
export function monthLabelKey(month: number): string {
  return `months.${MONTH_KEYS[month - 1]}`;
}

/** `months.mar`: l'abbreviazione del mese per gli assi dei grafici. */
export function monthShortLabelKey(month: number): string {
  return `months.${MONTH_SHORT_KEYS[month - 1]}`;
}

/** Il mese di un giorno di calendario (`YYYY-MM-DD`), senza fusi di mezzo. */
export function toUsagePeriod(day: DateString): UsagePeriod {
  const [year, month] = day.split('-');
  return { year: Number(year), month: Number(month) };
}

/** Il mese spostato di `months` mesi (negativo = all'indietro). */
export function shiftUsagePeriod(
  period: UsagePeriod,
  months: number
): UsagePeriod {
  const index = period.year * MONTHS_PER_YEAR + period.month - 1 + months;
  return {
    year: Math.floor(index / MONTHS_PER_YEAR),
    month: (index % MONTHS_PER_YEAR) + 1,
  };
}

export function isSameUsagePeriod(a: UsagePeriod, b: UsagePeriod): boolean {
  return a.year === b.year && a.month === b.month;
}

/** Vero se `a` viene dopo `b`. */
export function isUsagePeriodAfter(a: UsagePeriod, b: UsagePeriod): boolean {
  return (
    a.year * MONTHS_PER_YEAR + a.month > b.year * MONTHS_PER_YEAR + b.month
  );
}

/** Gli anni del selettore, dal corrente all'indietro. */
export function usageYearOptions(currentYear: number): number[] {
  return Array.from(
    { length: USAGE_YEARS_SHOWN },
    (_, index) => currentYear - index
  );
}

/** Un intero dall'URL (stringa o numero), `undefined` se non lo è. */
function toSearchInteger(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : undefined;
}

/**
 * L'anno scelto nell'URL, se è fra quelli del selettore; altrimenti il
 * corrente. Un anno scritto a mano fuori elenco non resta a video senza un
 * controllo che lo mostri.
 */
export function resolveUsageYear(value: unknown, currentYear: number): number {
  const year = toSearchInteger(value);
  return year !== undefined && usageYearOptions(currentYear).includes(year)
    ? year
    : currentYear;
}

/** Il mese scelto nell'URL (1-12), `undefined` se manca o non è valido. */
export function resolveUsageMonth(value: unknown): number | undefined {
  const month = toSearchInteger(value);
  return month !== undefined && USAGE_MONTHS.includes(month)
    ? month
    : undefined;
}

/** Il mese, o il corrente se finirebbe nel futuro (che non ha dati). */
export function clampUsagePeriod(
  candidate: UsagePeriod,
  current: UsagePeriod
): UsagePeriod {
  return isUsagePeriodAfter(candidate, current) ? current : candidate;
}

/**
 * Il mese mostrato dalla pagina dell'utilizzo AI: quello dell'URL, se valido
 * e non nel futuro, altrimenti il mese corrente.
 */
export function resolveUsagePeriod(
  search: { year?: unknown; month?: unknown },
  current: UsagePeriod
): UsagePeriod {
  return clampUsagePeriod(
    {
      year: resolveUsageYear(search.year, current.year),
      month: resolveUsageMonth(search.month) ?? current.month,
    },
    current
  );
}
